
import { jitsiLocalStorage } from '@jitsi/js-utils/jitsi-local-storage';

import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference';
import { setIsSwitchingBreakoutRoom, setIsSwitchingToMainRoom } from '../base/mtApi';
import { MiddlewareRegistry } from '../base/redux';
import { getLocalVideoTrack, isLocalCameraTrackMuted } from '../base/tracks';

import {
    BACKGROUND_ENABLED,
    RESET_VIRTUAL_BACKGROUND,
    SET_VIRTUAL_BACKGROUND
} from './actionTypes';
import {
    setVirtualBackground, toggleBackgroundEffect
} from './actions';
import logger from './logger';
import { getVirtualBackgroundOptions } from './reducer';

const VIRTUAL_BG_STATE_KEY = 'features/virtual-background/state';

/**
 * Retrieves the virtual background state to apply upon joining a conference.
 *
 * @param {Object} state - Redux state.
 * @param {boolean} isSwitchingRoom - Whether the user is switching rooms.
 * @returns {Object|null}
 */
function _getVirtualBgToApply(state, isSwitchingRoom) {
    if (isSwitchingRoom) {
        try {
            const savedBgState = jitsiLocalStorage.getItem(VIRTUAL_BG_STATE_KEY);

            if (savedBgState) {
                const parsedState = JSON.parse(savedBgState);

                if (parsedState?.backgroundEffectEnabled) {
                    logger.info('Restoring virtual background from localStorage after room switch');

                    return parsedState;
                }

                logger.info('Virtual background in localStorage is disabled, skipping restore');

                return null;
            }
        } catch (error) {
            logger.warn('Failed to restore virtual background from localStorage:', error);
        }

        return null;
    }

    const virtualBg = getVirtualBackgroundOptions(state);

    if (virtualBg.backgroundEffectEnabled) {
        const data = jitsiLocalStorage.getItem('features/virtual-background/images');
        const parser = JSON.parse(data || '{}');

        if (Object.keys(parser).length > 0) {
            return {
                backgroundEffectEnabled: true,
                backgroundType: 'image',
                virtualSource: parser.src,
                selectedThumbnail: parser.id || 'custom'
            };
        }

        return virtualBg;
    }

    return null;
}

/**
 * Implements the middleware of the Virtual Background feature.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { getState, dispatch } = store;
    const state = getState();
    const tracks = state['features/base/tracks'];

    switch (action.type) {

    case SET_VIRTUAL_BACKGROUND:
    case BACKGROUND_ENABLED: {
        const { backgroundEffectEnabled } = action;

        if (backgroundEffectEnabled === false) {
            try {
                jitsiLocalStorage.removeItem(VIRTUAL_BG_STATE_KEY);
                logger.info('Removed virtual background state from localStorage (background effect disabled)');
            } catch (error) {
                logger.warn('Failed to remove virtual background from localStorage:', error);
            }
        }
        break;
    }

    case RESET_VIRTUAL_BACKGROUND: {
        try {
            jitsiLocalStorage.removeItem(VIRTUAL_BG_STATE_KEY);
            jitsiLocalStorage.removeItem('features/virtual-background/images');
            logger.info('Reset virtual background state in localStorage');
        } catch (error) {
            logger.warn('Failed to clear virtual background state from localStorage:', error);
        }
        break;
    }

    case CONFERENCE_JOINED: {
        const isSwitchingBreakoutRoom = state['features/base/mtApi']?.isSwitchingBreakOutRoom;
        const isSwitchingToMainRoom = state['features/base/mtApi']?.isSwitchingToMainRoom;

        // The join is done, so the switch is over. This used to wait for the virtual background below, and applying
        // it can wait on the new media session - setEffect goes through its modification queue, which stays paused
        // until the session is accepted - so when that stalled, "Joining breakout room..." never went away.
        isSwitchingBreakoutRoom && dispatch(setIsSwitchingBreakoutRoom(false));
        isSwitchingToMainRoom && dispatch(setIsSwitchingToMainRoom(false));

        if (!isLocalCameraTrackMuted(tracks)) {
            const virtualBgToApply
                = _getVirtualBgToApply(state, isSwitchingBreakoutRoom || isSwitchingToMainRoom);

            if (virtualBgToApply?.backgroundEffectEnabled) {
                dispatch(setVirtualBackground(virtualBgToApply));
                const localTrack = getLocalVideoTrack(getState()['features/base/tracks'])?.mhTrack;
                const currentEffect = localTrack?._streamEffect;

                // On web the camera normally has it already: createLocalTracksF runs loadEffects, which builds the
                // effect from this same redux state. Web effects have no `_isActive` flag, so requiring it to be
                // truthy (as this used to) stacked a second effect onto the camera, racing the first for the shared
                // segmentation model. Native builds no effects at track creation and its effect does keep the flag,
                // false once its pipeline has stopped - re-apply in that case, as before.
                if (localTrack && (!currentEffect || currentEffect._isActive === false)) {
                    dispatch(toggleBackgroundEffect(virtualBgToApply, localTrack, true))
                        .catch(error => logger.error('Failed to apply the virtual background after joining', error));
                }
            }
        }
        break;
    }

    case CONFERENCE_LEFT:
        // Cleanup virtual background when leaving conference
        (async () => {
            try {
                const isSwitchingBreakoutRoom = state['features/base/mtApi']?.isSwitchingBreakOutRoom;
                const isSwitchingToMainRoom = state['features/base/mtApi']?.isSwitchingToMainRoom;
                const isSwitchingRoom = isSwitchingBreakoutRoom || isSwitchingToMainRoom;
                const virtualBg = getVirtualBackgroundOptions(state);

                if (isSwitchingRoom) {
                    if (virtualBg.backgroundEffectEnabled) {
                        try {
                            jitsiLocalStorage.setItem(VIRTUAL_BG_STATE_KEY, JSON.stringify(virtualBg));
                            logger.info('Saved virtual background state to localStorage for room switch');
                        } catch (error) {
                            logger.warn('Failed to save virtual background to localStorage:', error);
                        }
                    } else {
                        try {
                            jitsiLocalStorage.removeItem(VIRTUAL_BG_STATE_KEY);
                            logger.info('Removed virtual background state from localStorage for room switch (effect disabled)');
                        } catch (error) {
                            logger.warn('Failed to remove virtual background from localStorage:', error);
                        }
                    }
                } else {
                    const localTrack = getLocalVideoTrack(tracks)?.mhTrack;

                    if (localTrack && localTrack.setEffect) {
                        await localTrack.setEffect(undefined);
                    }

                    //  dispatch(backgroundEnabled(false));
                    // dispatch(resetVirtualBackground());
                }
            } catch (error) {
                logger.error('Error cleaning up virtual background on conference leave:', error);
            }
        })();
        break;

    }

    return next(action);
});
